import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin, Youtube } from 'lucide-react';

// WhatsApp Icon
const WhatsAppIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const navigate = useNavigate();

  // Navigate and scroll to top
  const handleNavigation = (path) => (e) => {
    e.preventDefault();
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-black text-white border-t border-gray-900">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_enrigo-creative/artifacts/19zqfisl_EA%20Logo.png" 
              alt="Enrigo" 
              className="h-16 w-auto mb-6"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              Creative solutions for print, digital, and beyond. Transforming brands with strategic design.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#F4B400] font-semibold mb-6 text-lg">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a href="/about" onClick={handleNavigation('/about')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  About Us
                </a>
              </li>
              <li>
                <a href="/portfolio" onClick={handleNavigation('/portfolio')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Portfolio
                </a>
              </li>
              <li>
                <a href="/shop" onClick={handleNavigation('/shop')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Services & Shop
                </a>
              </li>
              <li>
                <a href="/social" onClick={handleNavigation('/social')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Social Media
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-[#F4B400] font-semibold mb-6 text-lg">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="/blog" onClick={handleNavigation('/blog')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Blog
                </a>
              </li>
              <li>
                <a href="/contact" onClick={handleNavigation('/contact')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Contact
                </a>
              </li>
              <li>
                <a href="/login" onClick={handleNavigation('/login')} className="text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                  Login
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[#F4B400] font-semibold mb-6 text-lg">Get in Touch</h3>
            <div className="space-y-4">
              <a href="tel:+18608470649" className="flex items-center text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                <Phone size={16} className="mr-3 text-[#F4B400]" />
                (860) 847-0649
              </a>
              <a 
                href="https://wa.me/18608470649" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-gray-400 hover:text-green-400 transition-colors text-sm"
              >
                <span className="mr-3 text-green-500"><WhatsAppIcon size={16} /></span>
                WhatsApp Business
              </a>
              <a href="mailto:enrigo@ealagon.com" className="flex items-center text-gray-400 hover:text-[#F4B400] transition-colors text-sm">
                <Mail size={16} className="mr-3 text-[#F4B400]" />
                enrigo@ealagon.com
              </a>
              <div className="flex items-start text-gray-400 text-sm">
                <MapPin size={16} className="mr-3 mt-1 text-[#F4B400] flex-shrink-0" />
                <span>30 Airport Rd<br />Hartford, CT 06114</span>
              </div>
            </div>
            <div className="flex space-x-4 mt-6">
              <a 
                href="https://www.instagram.com/enrigo_us" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#F4B400] hover:text-white transition-colors"
                title="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/enrigous" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#F4B400] hover:text-white transition-colors"
                title="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.youtube.com/@enrigo-nc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#F4B400] hover:text-white transition-colors"
                title="YouTube"
              >
                <Youtube size={20} />
              </a>
              <a 
                href="https://wa.me/18608470649" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-500 hover:text-white transition-colors"
                title="WhatsApp"
              >
                <WhatsAppIcon size={20} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-900 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Enrigo. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <a href="/privacy" onClick={handleNavigation('/privacy')} className="text-gray-500 hover:text-[#F4B400] transition-colors">Privacy Policy</a>
              <span className="text-gray-800">&middot;</span>
              <a href="/terms" onClick={handleNavigation('/terms')} className="text-gray-500 hover:text-[#F4B400] transition-colors">Terms of Service</a>
              <span className="text-gray-800">&middot;</span>
              <a href="/cookies" onClick={handleNavigation('/cookies')} className="text-gray-500 hover:text-[#F4B400] transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
